import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { useProjectStore } from '@/stores/projectStore';
import { useAuthStore } from '@/stores/authStore';
import { handleApiError } from '@/services/api';
import type { ProjectFormData, TaskFormData } from '@/types';

export function useProjects() {
  const queryClient = useQueryClient();
  const { setProjects, setError } = useProjectStore();
  const { isAuthenticated } = useAuthStore();

  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
    staleTime: 30 * 1000, // 30 seconds
    enabled: isAuthenticated, // Only fetch when authenticated
  });

  const createProjectMutation = useMutation({
    mutationFn: (data: ProjectFormData) => projectService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      setError(handleApiError(error));
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProjectFormData> }) =>
      projectService.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      setError(handleApiError(error));
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      setError(handleApiError(error));
    },
  });

  // Update store when query data changes
  useEffect(() => {
    if (projectsQuery.data) {
      setProjects(projectsQuery.data.data);
    }
  }, [projectsQuery.data, setProjects]);

  return {
    projects: projectsQuery.data?.data ?? [],
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error ? handleApiError(projectsQuery.error) : null,
    createProject: createProjectMutation.mutateAsync,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
    refetch: projectsQuery.refetch,
  };
}

export function useProject(projectId: string) {
  const queryClient = useQueryClient();
  const { setCurrentProject, setTasks, setSpecDocument, setWorkflow, setPrototype } =
    useProjectStore();
  const { isAuthenticated } = useAuthStore();

  const isEnabled = isAuthenticated && !!projectId;

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(projectId),
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  const tasksQuery = useQuery({
    queryKey: ['tasks', projectId],
    queryFn: () => projectService.getTasks(projectId),
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  const specQuery = useQuery({
    queryKey: ['spec', projectId],
    queryFn: () => projectService.getSpecDocument(projectId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
    retry: false,
  });

  const workflowQuery = useQuery({
    queryKey: ['workflow', projectId],
    queryFn: () => projectService.getWorkflow(projectId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
    retry: false,
  });

  const prototypeQuery = useQuery({
    queryKey: ['prototype', projectId],
    queryFn: () => projectService.getPrototype(projectId),
    enabled: isEnabled,
    staleTime: 60 * 1000,
    retry: false,
  });

  const updateProjectMutation = useMutation({
    mutationFn: (data: Partial<ProjectFormData>) => projectService.updateProject(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data: TaskFormData) => projectService.createTask(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: Partial<TaskFormData> }) =>
      projectService.updateTask(projectId, taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
    },
  });

  const updateSpecMutation = useMutation({
    mutationFn: (content: string) => {
      const specId = specQuery.data?.id;
      return projectService.updateSpecDocument(projectId, content, specId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spec', projectId] });
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: (workflow: Parameters<typeof projectService.updateWorkflow>[1]) => {
      const workflowId = workflowQuery.data?.id;
      return projectService.updateWorkflow(projectId, workflow, workflowId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', projectId] });
    },
  });

  const updatePrototypeMutation = useMutation({
    mutationFn: (data: { content: string; name?: string; language?: 'html' | 'jsx' | 'tsx' | 'vue' }) => {
      const prototypeId = prototypeQuery.data?.id;
      return projectService.updatePrototype(
        projectId,
        {
          content: data.content,
          name: data.name || 'Prototype',
          language: data.language || 'tsx',
          artifact_type: 'prototype',
        },
        prototypeId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prototype', projectId] });
    },
  });

  // Update stores when query data changes
  useEffect(() => {
    if (projectQuery.data) {
      setCurrentProject(projectQuery.data);
    }
  }, [projectQuery.data, setCurrentProject]);

  useEffect(() => {
    if (tasksQuery.data) {
      setTasks(tasksQuery.data);
    }
  }, [tasksQuery.data, setTasks]);

  useEffect(() => {
    if (specQuery.data) {
      setSpecDocument(specQuery.data);
    }
  }, [specQuery.data, setSpecDocument]);

  useEffect(() => {
    if (workflowQuery.data) {
      setWorkflow(workflowQuery.data);
    }
  }, [workflowQuery.data, setWorkflow]);

  useEffect(() => {
    if (prototypeQuery.data) {
      setPrototype(prototypeQuery.data);
    }
  }, [prototypeQuery.data, setPrototype]);

  return {
    project: projectQuery.data,
    tasks: tasksQuery.data ?? [],
    spec: specQuery.data,
    workflow: workflowQuery.data,
    prototype: prototypeQuery.data,
    isLoading:
      projectQuery.isLoading ||
      tasksQuery.isLoading ||
      specQuery.isLoading ||
      workflowQuery.isLoading,
    updateProject: updateProjectMutation.mutateAsync,
    isUpdatingProject: updateProjectMutation.isPending,
    createTask: createTaskMutation.mutateAsync,
    updateTask: updateTaskMutation.mutateAsync,
    updateSpec: updateSpecMutation.mutateAsync,
    updateWorkflow: updateWorkflowMutation.mutateAsync,
    updatePrototype: updatePrototypeMutation.mutateAsync,
    isUpdatingPrototype: updatePrototypeMutation.isPending,
    refetch: () => {
      projectQuery.refetch();
      tasksQuery.refetch();
      specQuery.refetch();
      workflowQuery.refetch();
      prototypeQuery.refetch();
    },
  };
}
